import { Component, EventEmitter, Input, Output } from '@angular/core';
import { EstadoDePedido, ESTADO_PEDIDO_CONFIG } from '../../../../enums/EstadoDePedido.enum';
import { PedidoConsultaDTO, PedidoDTO } from '../../../../models/pedidos/pedido.model';

/** Opcao exibida na lista de transicoes. */
export interface OpcaoEstadoPedido {
  label: string;
  value: EstadoDePedido | string;
  acao: string;
  /**
   * Estado a que a opcao leva. Icone e cor do chip saem daqui (R1/R2) — por
   * isso ate "Entregar" e "Entregar e Finalizar", que sao endpoints proprios e
   * nao transicoes diretas, precisam declarar seu destino.
   */
  estadoDestino: EstadoDePedido;
}

/** Acoes que nao sao transicao de estado e sim endpoint proprio. */
export const ACAO_ENTREGAR = '__ENTREGAR__';
export const ACAO_ENTREGAR_E_FINALIZAR = '__ENTREGAR_E_FINALIZAR__';

/** Normaliza o estado: o backend serializa o enum ora como string, ora como objeto. */
function resolverEstado(estado: any): EstadoDePedido {
  if (typeof estado === 'object' && estado !== null) {
    return (estado.enum || estado.name) as EstadoDePedido;
  }
  return estado as EstadoDePedido;
}

function resolverTipoEntrega(pedido: any): string | undefined {
  const raw = pedido?.tipoEntrega;
  return typeof raw === 'object' && raw !== null ? raw.enum || raw.name : raw;
}

/** Entregar e finalizar de uma vez so faz sentido se nao restar saldo. */
export function podeEntregarEFinalizar(pedido: PedidoDTO | PedidoConsultaDTO | null): boolean {
  if (!pedido) return false;
  const estado = resolverEstado(pedido.estadoDePedido);
  const estadoPermitido = estado === EstadoDePedido.A_CAMINHO
    || estado === EstadoDePedido.AGUARDANDO_RETIRADA;
  if (!estadoPermitido) return false;
  return (pedido.itens || []).every(i => (i.saldo ?? 0) === 0);
}

/**
 * Transicoes validas a partir do estado atual, ja rotuladas.
 *
 * Exportada porque o card e o rodape do detalhe precisam saber se HA opcoes
 * antes de oferecer o botao — mantem a regra em um lugar so.
 */
export function calcularEstadosDisponiveis(
  pedido: PedidoDTO | PedidoConsultaDTO | null,
  texts: any,
): OpcaoEstadoPedido[] {
  if (!pedido) return [];

  const estado = resolverEstado(pedido.estadoDePedido);

  const acaoMap: Record<string, string> = {
    [EstadoDePedido.EM_PRODUCAO]: 'enviarParaProducao',
    [EstadoDePedido.A_CAMINHO]: 'enviarACaminho',
    [EstadoDePedido.AGUARDANDO_RETIRADA]: 'enviarAguardandoRetirada',
    [EstadoDePedido.CANCELADO]: 'cancelar',
  };

  // De EM_PRODUCAO o proximo estado depende do tipo de entrega.
  const transicaoEmProducao = (): EstadoDePedido[] => {
    const tipoEntrega = resolverTipoEntrega(pedido);
    if (tipoEntrega === 'DELIVERY') {
      return [EstadoDePedido.A_CAMINHO, EstadoDePedido.CANCELADO];
    }
    // Retirada e consumo no local: o cliente esta no estabelecimento, nada vai "a caminho".
    if (tipoEntrega === 'RETIRADA_NO_LOCAL' || tipoEntrega === 'CONSUMO_NO_LOCAL') {
      return [EstadoDePedido.AGUARDANDO_RETIRADA, EstadoDePedido.CANCELADO];
    }
    // Sem tipoEntrega definido, oferece os dois caminhos.
    return [EstadoDePedido.A_CAMINHO, EstadoDePedido.AGUARDANDO_RETIRADA, EstadoDePedido.CANCELADO];
  };

  const transicoes: Record<string, EstadoDePedido[]> = {
    [EstadoDePedido.ABERTO]: [EstadoDePedido.EM_PRODUCAO, EstadoDePedido.CANCELADO],
    [EstadoDePedido.EM_PRODUCAO]: transicaoEmProducao(),
    [EstadoDePedido.A_CAMINHO]: [EstadoDePedido.CANCELADO],
    [EstadoDePedido.AGUARDANDO_RETIRADA]: [EstadoDePedido.CANCELADO],
  };

  const opcoes: OpcaoEstadoPedido[] = [];

  if (estado === EstadoDePedido.A_CAMINHO || estado === EstadoDePedido.AGUARDANDO_RETIRADA) {
    opcoes.push({
      label: texts?.botoes?.entregar || 'Entregar',
      value: ACAO_ENTREGAR,
      acao: ACAO_ENTREGAR,
      estadoDestino: EstadoDePedido.ENTREGUE,
    });
  }

  if (podeEntregarEFinalizar(pedido)) {
    opcoes.push({
      label: texts?.botoes?.entregar_e_finalizar || 'Entregar e Finalizar',
      value: ACAO_ENTREGAR_E_FINALIZAR,
      acao: ACAO_ENTREGAR_E_FINALIZAR,
      estadoDestino: EstadoDePedido.FINALIZADO,
    });
  }

  (transicoes[estado] || []).forEach(e => {
    opcoes.push({
      label: texts?.estados?.[e] || e,
      value: e,
      acao: acaoMap[e] || '',
      estadoDestino: e,
    });
  });

  return opcoes;
}

/**
 * Seletor de transicao de estado do pedido, usado pelo card da listagem e pelo
 * pedido-detalhe-dialog.
 *
 * Aceita `PedidoDTO` e `PedidoConsultaDTO`: a decisao usa apenas
 * estadoDePedido, tipoEntrega e itens[].saldo, presentes nos dois — por isso o
 * card abre o seletor sem precisar carregar o detalhe.
 */
@Component({
  selector: 'app-pedido-estados-dialog',
  templateUrl: './pedido-estados-dialog.component.html',
  styleUrls: ['./pedido-estados-dialog.component.scss'],
})
export class PedidoEstadosDialogComponent {

  @Input() visivel = false;
  @Input() pedido: PedidoDTO | PedidoConsultaDTO | null = null;
  /** Bloco de traducoes ({ botoes, estados, mensagens }). */
  @Input() texts: any = {};

  @Output() visivelChange = new EventEmitter<boolean>();
  /** Emite a opcao escolhida; quem consome decide o que fazer com a acao. */
  @Output() acaoEscolhida = new EventEmitter<{ pedido: PedidoDTO | PedidoConsultaDTO; opcao: OpcaoEstadoPedido }>();

  fechar(): void {
    this.visivel = false;
    this.visivelChange.emit(false);
  }

  onSelecionar(opcao: OpcaoEstadoPedido): void {
    if (!this.pedido) return;
    this.acaoEscolhida.emit({ pedido: this.pedido, opcao });
    this.fechar();
  }

  /** Delega para a funcao exportada, unica fonte da regra de transicao. */
  estadosDisponiveis(): OpcaoEstadoPedido[] {
    return calcularEstadosDisponiveis(this.pedido, this.texts);
  }

  // --- Aparencia das opcoes -------------------------------------------------
  // Tudo sai de `estadoDestino`: o chip representa o estado a que a opcao leva,
  // entao usa o icone e a cor DELE. Antes as acoes especiais recebiam um verde
  // fixo, o que dava ao mesmo destino cores diferentes no card e no seletor.

  getCorOpcao(opcao: OpcaoEstadoPedido): string {
    return ESTADO_PEDIDO_CONFIG[opcao.estadoDestino]?.cor || '#999';
  }

  getFundoOpcao(opcao: OpcaoEstadoPedido): string {
    const config = ESTADO_PEDIDO_CONFIG[opcao.estadoDestino];
    return config?.gradiente || config?.cor || '#999';
  }

  getIconeOpcao(opcao: OpcaoEstadoPedido): string {
    return ESTADO_PEDIDO_CONFIG[opcao.estadoDestino]?.icone || 'pi pi-circle';
  }
}
